/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, Input, NgZone, OnChanges, OnInit, SimpleChanges, ViewChild, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import {resourceOptionParams} from '../../common/controller/signal-admin-resource-api';
import {StateService} from '@uirouter/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import template from './report-requester.component.html';
import {ADN_REPORT_TYPES} from '../report-types';
import {BsDatepickerConfig, BsDatepickerModule, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {
  applyNetworkCalendarWeekStart,
  createAdnBsDatepickerConfig
} from '../../common/directives/form-controls/bs-datepicker-locale';
import {LocaleDatePipe} from '../../common/filters/locale-date.pipe';
import {TranslateService} from '@ngx-translate/core';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {getIntlLocaleData, AngularLocaleData} from '../../common/locale/intl-locale-data';
import {EarningsAccountApiService} from '../../earnings-account/earnings-account-api.service';
import {UserApiService} from '../../admin/user/user-api.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {SearchApiService} from '../../common/controller/search-api.service';
import {EventTimerComponent} from '../../common/controller/event-timer.component';
import {ReportExecutionApiService, ReportExecutionModel} from '../report-execution-api.service';
import {SiteGroupApiService} from '../../site-group/site-group-api.service';
import {ReportTemplateApiService} from '../../report-template/report-template-api.service';
import {APP_SHARED} from '../../common/app-shared';
import {AdnSelectComponent} from '../../common/directives/adn-select/adn-select.component';
import {ReportHistoryComponent} from '../report-history.component';
import {ReportStatusComponent} from '../report-status-component';

@Component({
  standalone: true,
  imports: [
    ...APP_SHARED,
    FormsModule,
    UIRouterModule,
    ButtonsModule,
    BsDatepickerModule,
    EventTimerComponent,
    AdnSelectComponent,
    ReportHistoryComponent,
    ReportStatusComponent
  ],
  selector: 'adn-report-requester',
  template
})
export class ReportRequesterComponent implements OnInit, OnChanges {
  @Input() currency: string;
  @Input() collapsible = false;
  @Input() obj: any;
  @Input() reportType: string;

  isSmall: boolean;
  selectedReportType: any;
  private readonly reportTemplatesState = signal<any[]>([]);
  private readonly childReportTemplatesState = signal<any[]>([]);
  private readonly earningsAccountsState = signal<any[] | undefined>(undefined);
  private readonly siteGroupsState = signal<any[] | undefined>(undefined);
  private readonly usersState = signal<any[] | undefined>(undefined);
  private readonly templatesLoadedState = signal(false);
  private readonly lineItemResultsState = signal<any[]>([]);
  private readonly creativeSetResultsState = signal<any[]>([]);
  private readonly orderResultsState = signal<any[]>([]);
  private readonly advertiserResultsState = signal<any[]>([]);
  private readonly adUnitResultsState = signal<any[]>([]);
  private readonly siteResultsState = signal<any[]>([]);
  private readonly lineItemListResultsState = signal<any[]>([]);
  private readonly newReportExecutionsState = signal<ReportExecutionModel[]>([]);

  get reportTemplates(): any[] { return this.reportTemplatesState(); }
  set reportTemplates(value: any[]) { this.reportTemplatesState.set(value || []); }
  get childReportTemplates(): any[] { return this.childReportTemplatesState(); }
  set childReportTemplates(value: any[]) { this.childReportTemplatesState.set(value || []); }
  get earningsAccounts(): any[] | undefined { return this.earningsAccountsState(); }
  set earningsAccounts(value: any[] | undefined) { this.earningsAccountsState.set(value); }
  get siteGroups(): any[] | undefined { return this.siteGroupsState(); }
  set siteGroups(value: any[] | undefined) { this.siteGroupsState.set(value); }
  get users(): any[] | undefined { return this.usersState(); }
  set users(value: any[] | undefined) { this.usersState.set(value); }
  get templatesLoaded(): boolean { return this.templatesLoadedState(); }
  set templatesLoaded(value: boolean) { this.templatesLoadedState.set(value); }
  get lineItemResults(): any[] { return this.lineItemResultsState(); }
  set lineItemResults(value: any[]) { this.lineItemResultsState.set(value || []); }
  get creativeSetResults(): any[] { return this.creativeSetResultsState(); }
  set creativeSetResults(value: any[]) { this.creativeSetResultsState.set(value || []); }
  get orderResults(): any[] { return this.orderResultsState(); }
  set orderResults(value: any[]) { this.orderResultsState.set(value || []); }
  get advertiserResults(): any[] { return this.advertiserResultsState(); }
  set advertiserResults(value: any[]) { this.advertiserResultsState.set(value || []); }
  get adUnitResults(): any[] { return this.adUnitResultsState(); }
  set adUnitResults(value: any[]) { this.adUnitResultsState.set(value || []); }
  get siteResults(): any[] { return this.siteResultsState(); }
  set siteResults(value: any[]) { this.siteResultsState.set(value || []); }
  get lineItemListResults(): any[] { return this.lineItemListResultsState(); }
  set lineItemListResults(value: any[]) { this.lineItemListResultsState.set(value || []); }
  get newReportExecutions(): ReportExecutionModel[] { return this.newReportExecutionsState(); }
  set newReportExecutions(value: ReportExecutionModel[]) { this.newReportExecutionsState.set(value || []); }

  objectHolder: any;
  objectType = 'ORDER';
  perms: any;
  canSeeObjects: boolean;
  canExecute: boolean;
  canSeeAdvertiserReports: boolean;
  availableCurrencies: any[];
  reportTypes: string[];
  reportRequest: any;
  selReportType: any;
  requestReport: boolean;
  bsConfig: Partial<BsDatepickerConfig>;

  private allTemplates: any[] = [];
  private readonly timeZone: string;
  private readonly localeData: AngularLocaleData;
  private timerComponent: EventTimerComponent | undefined;
  private timerCheckPending = false;

  @ViewChild(EventTimerComponent)
  set eventTimer(component: EventTimerComponent | undefined) {
    this.timerComponent = component;
    if (component && this.timerCheckPending) {
      this.checkNewExecutions();
    }
  }

  constructor(
    private readonly localeDate: LocaleDatePipe,
    @Inject(LocalNetworkProfile) private LocalNetworkProfile: any,
    @Inject(UserApiService) private User: UserApiService,
    private readonly siteGroupApi: SiteGroupApiService,
    private readonly earningsAccountApi: EarningsAccountApiService,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    @Inject(LocalUserProfile) private localUserProfile: any,
    private readonly reportExecutionApi: ReportExecutionApiService,
    private readonly reportTemplateApi: ReportTemplateApiService,
    private readonly stateService: StateService,
    private readonly appLocale: AppLocaleService,
    private readonly searchApi: SearchApiService,
    private bsLocaleService: BsLocaleService,
    private readonly translate: TranslateService,
    private ngZone: NgZone,
  ) {
    this.timeZone = LocalNetworkProfile.get('timeZone') || 'UTC';
    this.localeData = getIntlLocaleData(this.appLocale.getLocaleId());
    this.bsConfig = this.createBsConfig();
  }

  reportTemplateHref(reportTemplateId: string): string {
    return this.stateService.href('app.rep.report-templates.report-template', {id: reportTemplateId});
  }

  private searchByType(searchElement: any, types: string, target: string): void {
    this.searchApi.search({q: searchElement.search, types, pageSize: 50}).subscribe((data) => {
      this.ngZone.run(() => {
        (this as any)[target] = data.searchResults || [];
      });
    });
  }

  readonly searchLineItems = (s: any) => this.searchByType(s, 'LineItem', 'lineItemResults');
  readonly searchCreativeSets = (s: any) => this.searchByType(s, 'CreativeSet', 'creativeSetResults');
  readonly searchOrders = (s: any) => this.searchByType(s, 'Order', 'orderResults');
  readonly searchAdvertisers = (s: any) => this.searchByType(s, 'Advertiser', 'advertiserResults');
  readonly searchAdUnits = (s: any) => this.searchByType(s, 'AdUnit', 'adUnitResults');
  readonly searchSites = (s: any) => this.searchByType(s, 'Site', 'siteResults');
  readonly searchLineItemList = (s: any) => this.searchByType(s, 'LineItem', 'lineItemListResults');

  private filterTemplates(reportType: string): any[] {
    return reportType ? _.filter(this.allTemplates, (t) => {
      return t.reportType === reportType || (reportType === 'LINE_ITEM' && t.reportType === 'SELF_SERVICE');
    }) : _.cloneDeep(this.allTemplates);
  }

  filterReportTemplates(newType?: string): void {
    const type = newType !== undefined ? newType : this.selectedReportType.type;
    this.reportTemplates = this.filterTemplates(type);
    if (this.objectHolder) {
      this.objectHolder.object = this.obj;
    }
    if (this.reportRequest) {
      this.reportRequest.reportTemplate = undefined;
      this.reportRequest.childReportTemplate = undefined;
    }
    this.selReportType = ADN_REPORT_TYPES[type];
  }

  startReport(): void {
    const reportTemplate = this.reportRequest.reportTemplate;
    if (!reportTemplate) { return; }
    const childReportTemplate = this.reportRequest.childReportTemplate;
    const objectName = ADN_REPORT_TYPES.NETWORK.id === this.selectedReportType.type ? 'Network' : _.get(this.objectHolder, 'object.name');
    const reportParams: any = {
      name: [reportTemplate.name, objectName, this.localeDate.transform(new Date(), 'shortDate')].join(' - '),
      reportTemplate: reportTemplate,
      childReportTemplate: childReportTemplate || null,
      parameters: {
        localePriority: 'descending',
        timeZone: this.timeZone,
        currency: this.currency || this.LocalNetworkProfile.get('defaultCurrency') || 'EUR',
        locale: this.localUserProfile.get('locale') || 'en_GB'
      }
    };
    const selReportType = ADN_REPORT_TYPES[this.selectedReportType.type];
    if ((selReportType.specifyDates || this.reportRequest.dates === true) && _.isDate(this.reportRequest.startDate)) {
      reportParams.parameters.startDate = this.toUtcApiDate(this.reportRequest.startDate);
      if (_.isDate(this.reportRequest.endDate)) {
        reportParams.parameters.endDate = this.toUtcApiDate(this.reportRequest.endDate, true);
      }
    }
    if (ADN_REPORT_TYPES.LINE_ITEM.id === this.selectedReportType.type) {
      reportParams.parameters.lineItemId = this.objectHolder.object.id;
      reportParams.relatedLineItems = [{id: this.objectHolder.object.id}];
    } else if (ADN_REPORT_TYPES.NETWORK.id === this.selectedReportType.type) {
      reportParams.parameters[selReportType.idKey] = this.LocalNetworkProfile.getNetworkId();
    } else if (ADN_REPORT_TYPES.LINE_ITEM_LIST.id === this.selectedReportType.type) {
      if (this.objectType === 'ORDER') {
        reportParams.parameters[selReportType.idKey] = _.get(this.objectHolder, ['object', 'id']);
      } else {
        reportParams.parameters[selReportType.idsKey] = _.map(_.get(this.objectHolder, ['lineItems'], []), (li: any) => li.id);
      }
    } else {
      reportParams.parameters[selReportType.idKey] = this.objectHolder.object.id;
    }

    this.reportExecutionApi.start(reportParams).subscribe((reportExecution) => {
      this.ngZone.run(() => {
        this.newReportExecutions = [reportExecution, ...this.newReportExecutions];
        this.checkNewExecutions(true);
      });
    });
  }

  private checkNewExecutions(cancelTimers?: boolean): void {
    if (!this.timerComponent) {
      this.timerCheckPending = true;
      return;
    }
    this.timerCheckPending = false;
    if (cancelTimers) {
      this.timerComponent.cancelTimers();
    }
    const found = _.find(this.newReportExecutions, (reportExecution) => {
      return this.reportExecutionApi.isAwaiting(reportExecution);
    });
    if (found) {
      this.timerComponent.refreshTimers();
    } else {
      this.timerComponent.cancelTimers();
    }
  }

  refreshReportExecutions(): void {
    const ids = _.map(this.newReportExecutions, 'id');
    if (!ids.length) {
      return;
    }
    this.timerComponent?.setRefreshing(true);
    this.reportExecutionApi.listByIds(ids).subscribe({
      next: (page) => {
        this.ngZone.run(() => {
          this.newReportExecutions = page.results;
          this.timerComponent?.setRefreshing(false);
          this.checkNewExecutions();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.timerComponent?.setRefreshing(false);
          this.checkNewExecutions();
        });
      }
    });
  }

  ngOnInit(): void {
    this.initReportRequester();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.selectedReportType) {
      return;
    }
    if (changes.reportType || changes.obj) {
      if (changes.obj && this.objectHolder) {
        this.objectHolder.object = this.obj;
      }
      this.initReportTypeSelection();
    }
  }

  private get resolvedReportType(): string {
    return this.reportType;
  }

  private initReportRequester(): void {
    applyNetworkCalendarWeekStart(
      this.bsLocaleService,
      this.LocalNetworkProfile.getDefaults()?.startDayCalendar,
      this.localeData
    );
    this.refreshBsConfig();
    this.isSmall = !!this.resolvedReportType;
    this.requestReport = !this.collapsible;
    this.selectedReportType = {};

    this.availableCurrencies = this.LocalNetworkProfile.get('availableCurrencies');
    const preferredCurrency = this.localUserProfile.getPreferredCurrency();
    this.currency = this.currency || preferredCurrency || this.LocalNetworkProfile.get('defaultCurrency');

    this.objectHolder = {};
    this.objectHolder.object = this.obj;

    this.perms = this.LocalUserPermissions.getAllVisPermissions();
    this.canSeeObjects = true;
    this.canExecute = this.perms.RUN_REPORTS || this.perms.AX_ADVERTISER || this.perms.AX_PUBLISHER;
    this.templatesLoaded = false;

    const networkDefaults = this.LocalNetworkProfile.getDefaults();
    this.canSeeAdvertiserReports = networkDefaults.runReportsOptions.indexOf('ADVERTISER_REPORTS') > -1;

    this.initReportTypeSelection();

    this.reportTypes = _.map(_.filter(ADN_REPORT_TYPES, (rt: any) => {
      if (rt.perms) {
        return !!_.find(_.isArray(rt.perms) ? rt.perms : [rt.perms], (perm: string) => {
          return this.perms[perm] === true;
        });
      }
      return rt.id !== 'SELF_SERVICE' || this.LocalNetworkProfile.isSuiPlatform();
    }), (rt: any) => rt.id);

    this.earningsAccountApi.listOptions().subscribe((page) => {
      this.earningsAccounts = page.results;
    });

    this.siteGroupApi.listAll().subscribe((page) => {
      this.siteGroups = page.results;
    });

    firstValueFrom(this.User.listOptions(resourceOptionParams({minimal: true}))).then((page: any) => {
      this.users = page.results;
    });

    this.reportTemplateApi.listAll().subscribe((page) => {
      this.allTemplates = _.cloneDeep(page.results);
      this.reportTemplates = this.filterTemplates(this.selectedReportType.type);
      this.childReportTemplates = this.filterTemplates('LINE_ITEM');
      this.templatesLoaded = true;
    });

    this.reportRequest = {dates: false};
    const networkNow = moment.tz(this.timeZone);
    this.reportRequest.startDate = this.toPickerDate(networkNow.clone().subtract(1, 'month'));
    this.reportRequest.endDate = this.toPickerDate(networkNow);

    this.newReportExecutions = [];

  }

  private toPickerDate(networkDate: moment.Moment): Date {
    return new Date(networkDate.year(), networkDate.month(), networkDate.date());
  }

  private createBsConfig(): Partial<BsDatepickerConfig> {
    return createAdnBsDatepickerConfig({
      shortDateFormat: this.localeData.DATETIME_FORMATS.shortDate,
      today: this.getNetworkTodayForHighlight(),
      todayButtonLabel: this.translate.instant('button.today')
    });
  }

  private refreshBsConfig(): void {
    this.bsConfig = this.createBsConfig();
  }

  private getNetworkTodayForHighlight(): Date {
    return this.toPickerDate(moment.tz(this.timeZone));
  }

  private toUtcApiDate(pickerDate: Date, endOfDay = false): Date {
    const networkDate = moment.tz({
      year: pickerDate.getFullYear(),
      month: pickerDate.getMonth(),
      day: pickerDate.getDate()
    }, this.timeZone);
    return (endOfDay ? networkDate.endOf('day') : networkDate.startOf('day')).utc().toDate();
  }

  private initReportTypeSelection(): void {
    if (!this.selectedReportType) {
      return;
    }
    this.selectedReportType.type = this.resolvedReportType || this.selectedReportType.type;
    this.selReportType = ADN_REPORT_TYPES[this.selectedReportType.type];
    if (this.allTemplates.length) {
      this.reportTemplates = this.filterTemplates(this.selectedReportType.type);
    }
    if (!this.selectedReportType.type) {
      if (this.perms?.MANAGE_ADVERTISING) {
        this.selectedReportType.type = 'LINE_ITEM';
      } else if (this.perms?.MANAGE_PUBLISHING) {
        this.selectedReportType.type = 'AD_UNIT';
      } else if (this.perms?.MANAGE_BUSINESS) {
        this.selectedReportType.type = 'ADVERTISER';
      } else if (this.perms?.MANAGE_SYSTEM) {
        this.selectedReportType.type = 'NETWORK';
      }
    }
    this.selReportType = ADN_REPORT_TYPES[this.selectedReportType.type];
  }
}
